import { useEffect, useState } from "react";
import { ArrowRight, ArrowLeft, X, ClipboardList, LineChart, Share2, Archive } from "lucide-react";

const STORAGE_KEY = "thc-onboarding-v1-dismissed";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Empieza por tu primer diagnóstico",
    body:
      "30 preguntas repartidas en 6 áreas clave. Se completa en pocos minutos y se guarda automáticamente mientras lo rellenas, así que puedes retomarlo cuando quieras.",
  },
  {
    icon: LineChart,
    title: "Aquí verás todos tus informes",
    body:
      "Cada diagnóstico queda listado con su puntuación, nivel y fecha. Puedes descargarlo en PDF, previsualizarlo sin descargar, o abrir el detalle completo con gráficos e interpretación.",
  },
  {
    icon: Share2,
    title: "Compara y comparte tu evolución",
    body:
      "Selecciona hasta 3 diagnósticos para verlos en paralelo y medir tu progreso. También puedes generar una imagen resumen lista para compartir en LinkedIn.",
  },
  {
    icon: Archive,
    title: "Papelera con red de seguridad",
    body:
      "Si borras un diagnóstico por error, se queda 30 días en la papelera antes de eliminarse definitivamente. Restaurarlo es un clic.",
  },
];

/**
 * Onboarding modal mostrado UNA sola vez tras el primer login (per-navegador via localStorage).
 * El usuario puede saltárselo o completar los 4 pasos. Ambos casos se marcan como visto.
 */
export default function OnboardingTour({ onDone }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function finish() {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_e) {
      // localStorage puede fallar en modo privado; no bloqueamos
    }
    onDone();
  }

  function next() {
    if (step < STEPS.length - 1) setStep(step + 1);
    else finish();
  }

  function prev() {
    if (step > 0) setStep(step - 1);
  }

  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 fade-in-up"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      data-testid="onboarding-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        <button
          onClick={finish}
          className="absolute top-6 right-6 md:top-8 md:right-8 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 text-gray-500 hover:text-gray-900 hover:bg-white transition-colors shadow-sm"
          data-testid="onboarding-close-btn"
          aria-label="Saltar tour"
        >
          <X className="w-5 h-5" strokeWidth={1.75} />
        </button>
        <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0f6e56 0%, #22a888 55%, #34d399 100%)" }}>
          <div className="px-8 py-10 md:px-10 md:py-12 relative z-10">
            <div className="w-16 h-16 rounded-2xl bg-white/15 border border-white/25 backdrop-blur-sm text-white flex items-center justify-center mb-6">
              <Icon className="w-7 h-7" strokeWidth={1.75} />
            </div>
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-white/85 mb-2">
              Paso {step + 1} de {STEPS.length}
            </div>
            <h2
              id="onboarding-title"
              className="font-display text-2xl md:text-3xl font-bold text-white leading-tight"
              data-testid="onboarding-step-title"
            >
              {s.title}
            </h2>
          </div>
        </div>
        <div className="px-8 py-7 md:px-10 md:py-8">
          <p
            className="text-[15px] text-gray-700 leading-relaxed"
            style={{ textAlign: "justify", hyphens: "auto" }}
            data-testid="onboarding-step-body"
          >
            {s.body}
          </p>
          <div className="mt-8 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? "w-6 bg-[#0f6e56]" : "w-1.5 bg-gray-200"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button onClick={prev} className="btn-ghost text-sm" data-testid="onboarding-prev-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Anterior
                </button>
              )}
              <button onClick={next} className="btn-primary text-sm py-2.5 px-5" data-testid="onboarding-next-btn">
                {step === STEPS.length - 1 ? "Entendido" : "Siguiente"}{" "}
                {step < STEPS.length - 1 && <ArrowRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook para saber si hay que mostrar el tour. Solo se muestra:
 * - Si nunca antes se descartó
 * - Si el usuario aún no tiene diagnósticos ni borradores (primer login real).
 */
export function shouldShowOnboarding({ hasAny }) {
  try {
    if (localStorage.getItem(STORAGE_KEY) === "1") return false;
  } catch (_e) {
    return false;
  }
  return !hasAny;
}
